import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  Injector,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import { map, Observable, withLatestFrom } from 'rxjs';
import { ToggleButton } from '../../../../../cross-team-shared/components/toggle-button-group/toggle-button-group.component';
import { KeyboardShortcutService } from '../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { VideoStore } from '../../../../../outputs/annotation/video/state/video-store';
import { toggleState } from '../../../../../shared/misc-utilities/store-utils';
import { shellConnectorToken } from '../../../../../team-apis/core/shell-connector';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { isProjectionEnabled } from '../../../../shared/utils/projection-utils';

@Component({
  selector: 'h3-enable-3d-shortcuts-toggle',
  templateUrl: './enable-3d-shortcuts-toggle.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class Enable3DShortcutsToggleComponent
  extends DirectiveSuperclass
  implements OnInit
{
  redirectShortcutsTo$!: Observable<string>;
  buttons$: Observable<ToggleButton[]>;

  private keyboardShortcutService = inject(KeyboardShortcutService);
  private shellConnector = inject(shellConnectorToken);
  private isProjectionEnabled$!: Observable<boolean>;

  constructor(
    injector: Injector,
    @Inject(outputInfoStoreToken) public outputInfoStore: Store<OutputInfo>,
    private videoStore: VideoStore,
    private taskWorkspaceStore: TaskWorkspaceStore,
  ) {
    super(injector);

    const { name: outputName } = this.outputInfoStore.state().output;
    this.isProjectionEnabled$ = this.taskWorkspaceStore('lutData').$.pipe(
      map((lutData) =>
        isProjectionEnabled(
          this.taskWorkspaceStore.state(),
          outputName,
          lutData,
        ),
      ),
    );
    this.buttons$ = this.buildButtons$();
  }

  ngOnInit() {
    this.redirectShortcutsTo$ = this.videoStore(
      'redirectShortcutsToPointCloud',
    ).$.pipe(map((redirected) => (redirected ? '3d' : '2d')));

    this.subscribeToKeyboardShortcut();
  }

  async onRedirectToPointCloudToggle(value: '3d' | '2d') {
    this.videoStore('redirectShortcutsToPointCloud').set(value === '3d');
    this.trackToggleEnable2DShortcutsEvent('button');
  }

  private buildButtons$() {
    return this.isProjectionEnabled$.pipe(
      map((isProjectionsEnabled) => {
        const disable = isProjectionsEnabled ? undefined : 'true';
        return [
          { label: '2D', value: '2d', tooltip: 'Enable 2D shortcuts', disable },
          { label: '3D', value: '3d', tooltip: 'Enable 3D shortcuts', disable },
        ];
      }),
    );
  }

  private subscribeToKeyboardShortcut() {
    this.subscribeTo(
      this.keyboardShortcutService
        .getMatchingEvent$('toggle3DShortcuts')
        .pipe(withLatestFrom(this.isProjectionEnabled$)),
      ([_, projectionEnabled]) => {
        if (!projectionEnabled) {
          return;
        }
        toggleState(this.videoStore('redirectShortcutsToPointCloud'));
        this.trackToggleEnable2DShortcutsEvent('keyboard_shortcut');
      },
    );
  }

  private trackToggleEnable2DShortcutsEvent(
    source: 'keyboard_shortcut' | 'button',
  ) {
    this.shellConnector.trackEvent({
      id: 'toggle_enable_3d_shortcuts',
      bucket: 'navigation',
      sub_bucket: 'frame',
      source,
      new_value: this.videoStore.state().redirectShortcutsToPointCloud,
    });
  }
}
